import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Bell, 
  CheckCircle, 
  History,
  LogOut,
  User,
  DollarSign,
  ChevronDown,
  CalendarDays
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const GuideLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const navItems = [
    { href: '/guide', icon: Bell, label: 'Requests', badge: true },
    { href: '/guide?view=accepted', icon: CheckCircle, label: 'Accepted' },
    { href: '/guide?view=availability', icon: CalendarDays, label: 'Calendar' },
    { href: '/guide?view=history', icon: History, label: 'History' },
    { href: '/guide/finance', icon: DollarSign, label: 'Finance' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-slate-900">{title || 'Guide Dashboard'}</h1>
              <p className="text-sm text-slate-500">Welcome, {user?.name}</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-1" data-testid="guide-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-indigo-700 font-semibold text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 border-b">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <p className="text-xs text-indigo-600 capitalize mt-1">Guide</p>
                </div>
                <DropdownMenuItem asChild>
                  <Link to="/guide?view=profile" className="flex items-center cursor-pointer" data-testid="guide-profile-link">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="guide-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="px-4 py-4">
        <div className="max-w-md mx-auto animate-fade-in">
          {children}
        </div>
      </main>
      
      {/* Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        <div className="flex justify-around items-center max-w-md mx-auto">
          {navItems.map((item) => {
            const isActive = location.pathname + location.search === item.href || 
                            (location.pathname === '/guide' && item.href === '/guide' && !location.search);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors",
                  isActive ? "text-indigo-600" : "text-slate-500"
                )}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default GuideLayout;
